/* =====================================================================
   KioskClockWidget — Public, session-less staff clock kiosk for the
   serving panel. Owner ask Feb 2026: "I want everyone to be clocking
   in and clocking out from serving panel. Make this system. Everyone
   should put in their code, selfie and clockin and same while
   clockout".

   Flow:
     1. Staff taps the action they want (In / Out / Break Start / Break End).
     2. They punch their 4-digit kiosk PIN on the on-screen keypad.
     3. The front camera fires up via getUserMedia, they tap "Capture",
        and a frame is grabbed onto a hidden <canvas> as a JPEG dataURL.
     4. We POST {pin, selfie_base64, action} to /staff/clock/kiosk-action.
        Backend resolves the user by PIN, runs Gemini face-match against
        the stored reference, and writes the clock record.
     5. The widget resets and shows a brief success / error toast.

   Notes:
     - This component does NOT depend on a logged-in user. It works on
       the floor tablet even after the previous staff has signed out.
     - The PIN field auto-clears after each submission so the next
       person doesn't see the previous PIN.
     - We deliberately request the FRONT camera (`facingMode: "user"`).
   ===================================================================== */
import React, { useEffect, useRef, useState, useCallback } from "react";
import { Camera, Clock, X, Coffee, Play, LogIn, LogOut, RotateCcw, ShieldCheck } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

const ACTIONS = [
  { key: "in",          label: "Clock In",     icon: LogIn,  tone: "emerald" },
  { key: "out",         label: "Clock Out",    icon: LogOut, tone: "amber"   },
  { key: "break_start", label: "Start Break",  icon: Coffee, tone: "blue"    },
  { key: "break_end",   label: "End Break",    icon: Play,   tone: "purple"  },
];

const TONE_CLASS = {
  emerald: "bg-emerald-500/15 hover:bg-emerald-500/25 border-emerald-500/40 text-emerald-200",
  amber:   "bg-amber-500/15 hover:bg-amber-500/25 border-amber-500/40 text-amber-200",
  blue:    "bg-blue-500/15 hover:bg-blue-500/25 border-blue-500/40 text-blue-200",
  purple:  "bg-purple-500/15 hover:bg-purple-500/25 border-purple-500/40 text-purple-200",
};

export default function KioskClockWidget() {
  const [phase, setPhase] = useState("idle");       // idle | pin | camera | submitting
  const [action, setAction] = useState(null);       // selected action object
  const [pin, setPin] = useState("");
  const [stream, setStream] = useState(null);
  const [snapshot, setSnapshot] = useState(null);   // dataURL
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const reset = useCallback(() => {
    setAction(null);
    setPin("");
    setSnapshot(null);
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
    }
    setStream(null);
    setPhase("idle");
  }, [stream]);

  // Start the front camera. Must be called SYNCHRONOUSLY from a user
  // gesture (button click) so Safari / iOS don't drop the request.
  // We DO NOT touch videoRef here — Samsung Chrome's render scheduling
  // means the <video> element isn't in the DOM yet when this resolves.
  // A dedicated useEffect (below) binds the stream once both `stream`
  // and the mounted <video> are available.
  const startCamera = useCallback(async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false,
      });
      setStream(s);
      setPhase("camera");
    } catch (err) {
      const name = err?.name || "";
      if (name === "NotAllowedError" || name === "SecurityError") {
        toast.error("Camera permission denied — enable it in your browser settings and try again.");
      } else if (name === "NotFoundError" || name === "OverconstrainedError") {
        toast.error("No front camera found on this device.");
      } else {
        toast.error(`Camera error: ${name || "unknown"}. Try a different browser.`);
      }
      reset();
    }
  }, [reset]);

  // Bind the live stream to the <video> element once BOTH exist. This
  // runs AFTER React renders the camera phase, so videoRef.current is
  // guaranteed to be the freshly mounted <video> tag. Without this,
  // Samsung Chrome shows a permanent black "Starting camera…" because
  // the previous bind ran before the element entered the DOM.
  useEffect(() => {
    if (phase !== "camera" || !stream || !videoRef.current) return;
    const v = videoRef.current;
    if (v.srcObject !== stream) {
      v.srcObject = stream;
    }
    // Some Android Chromes don't auto-play even with the autoPlay
    // attribute on a srcObject change — explicitly call play().
    const tryPlay = () => v.play().catch(() => {});
    if (v.readyState >= 1) {
      tryPlay();
    } else {
      v.onloadedmetadata = tryPlay;
    }
  }, [phase, stream]);

  // Pick an action → jump to PIN entry.
  const pickAction = (a) => {
    setAction(a);
    setPin("");
    setPhase("pin");
  };

  // PIN keypad press. Fourth digit triggers camera SYNCHRONOUSLY from
  // this click handler so Safari accepts the getUserMedia call as a
  // legitimate user gesture.
  const pressKey = (d) => {
    if (pin.length >= 4) return;
    const next = pin + d;
    setPin(next);
    if (next.length === 4) {
      // Don't await — keep the click handler synchronous-looking for Safari.
      startCamera();
    }
  };
  const backspace = () => setPin((prev) => prev.slice(0, -1));

  // Stop the stream whenever this component unmounts.
  useEffect(() => () => {
    if (stream) stream.getTracks().forEach((t) => t.stop());
  }, [stream]);

  // Capture a still frame from the live video onto the hidden canvas.
  const capture = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const v = videoRef.current;
    const c = canvasRef.current;
    const w = v.videoWidth || 480;
    const h = v.videoHeight || 360;
    c.width = w;
    c.height = h;
    const ctx = c.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(v, 0, 0, w, h);
    const dataUrl = c.toDataURL("image/jpeg", 0.85);
    setSnapshot(dataUrl);
  };

  // Re-take after a bad capture (lighting, angle).
  const retake = () => {
    setSnapshot(null);
  };

  // POST PIN + selfie to the public kiosk endpoint.
  const submit = async () => {
    if (!action || !snapshot || pin.length !== 4) return;
    setPhase("submitting");
    try {
      const { data } = await api.post("/staff/clock/kiosk-action", {
        pin,
        selfie_base64: snapshot,
        action: action.key,
      });
      const enrolledNote = data.enrolled ? " · reference enrolled" : "";
      const extra =
        data.total_hours !== undefined
          ? ` · ${data.total_hours.toFixed(2)}h`
          : data.break_minutes !== undefined
          ? ` · ${data.break_minutes} min break`
          : "";
      toast.success(
        `${data.message} · ${data.user_name || "Staff"}${extra}${enrolledNote}`,
        { duration: 6000 }
      );
      reset();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Kiosk action failed";
      toast.error(detail);
      // On face-match rejection ask them to retake first; if PIN was
      // wrong, drop them back to PIN entry.
      if (err?.response?.status === 403 && detail.toLowerCase().includes("face")) {
        setSnapshot(null);
        setPhase("camera");
      } else {
        reset();
      }
    }
  };

  // ============================================================
  // RENDER
  // ============================================================
  if (phase === "idle") {
    return (
      <div
        className="rounded-md border border-white/10 bg-bg-secondary/80 backdrop-blur-md p-4 sm:p-5"
        data-testid="kiosk-clock-widget"
      >
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck className="w-4 h-4 text-gold" />
          <p className="font-sans text-[11px] uppercase tracking-[0.18em] text-zinc-400">
            Staff Clock — PIN + Selfie
          </p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {ACTIONS.map((a) => {
            const Icon = a.icon;
            return (
              <button
                key={a.key}
                onClick={() => pickAction(a)}
                className={`h-16 rounded-sm border flex flex-col items-center justify-center gap-1 transition-colors ${TONE_CLASS[a.tone]}`}
                data-testid={`kiosk-action-${a.key}`}
              >
                <Icon className="w-4 h-4" />
                <span className="text-[11px] font-sans uppercase tracking-[0.12em]">
                  {a.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // ---- PIN keypad ----
  if (phase === "pin") {
    return (
      <div className="rounded-md border border-white/10 bg-bg-secondary p-4 sm:p-5" data-testid="kiosk-pin-pad">
        <div className="flex items-center justify-between mb-3">
          <p className="font-sans text-[11px] uppercase tracking-[0.18em] text-zinc-400">
            {action?.label} — Enter your 4-digit PIN
          </p>
          <button
            onClick={reset}
            className="text-zinc-500 hover:text-white"
            data-testid="kiosk-cancel"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex justify-center gap-2 mb-4">
          {[0, 1, 2, 3].map((i) => (
            <span
              key={i}
              className={`w-10 h-12 rounded-sm border flex items-center justify-center text-2xl font-serif ${pin[i] ? "border-gold/60 text-gold" : "border-white/10 text-zinc-700"}`}
              data-testid={`kiosk-pin-slot-${i}`}
            >
              {pin[i] ? "•" : ""}
            </span>
          ))}
        </div>
        <div className="grid grid-cols-3 gap-2 max-w-xs mx-auto">
          {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((d) => (
            <button
              key={d}
              onClick={() => pressKey(d)}
              className="h-12 rounded-sm border border-white/10 hover:bg-white/[0.04] text-white font-serif text-xl"
              data-testid={`kiosk-key-${d}`}
            >
              {d}
            </button>
          ))}
          <button
            onClick={backspace}
            className="h-12 rounded-sm border border-white/10 hover:bg-white/[0.04] text-zinc-400"
            data-testid="kiosk-key-back"
          >
            ⌫
          </button>
          <button
            onClick={() => pressKey("0")}
            className="h-12 rounded-sm border border-white/10 hover:bg-white/[0.04] text-white font-serif text-xl"
            data-testid="kiosk-key-0"
          >
            0
          </button>
          <button
            onClick={() => setPin("")}
            className="h-12 rounded-sm border border-white/10 hover:bg-white/[0.04] text-zinc-400"
            data-testid="kiosk-key-clear"
          >
            <RotateCcw className="w-4 h-4 mx-auto" />
          </button>
        </div>
      </div>
    );
  }

  // ---- Camera capture / submit ----
  if (phase === "camera" || phase === "submitting") {
    return (
      <div className="rounded-md border border-white/10 bg-bg-secondary p-4 sm:p-5" data-testid="kiosk-camera">
        <div className="flex items-center justify-between mb-3">
          <p className="font-sans text-[11px] uppercase tracking-[0.18em] text-zinc-400">
            {action?.label} — {snapshot ? "Confirm selfie" : "Look at the camera"}
          </p>
          <button
            onClick={reset}
            className="text-zinc-500 hover:text-white"
            data-testid="kiosk-cancel"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="relative w-full max-w-sm mx-auto aspect-[4/3] bg-black/60 rounded-sm overflow-hidden">
          {!snapshot && (
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover scale-x-[-1]"
              data-testid="kiosk-video"
            />
          )}
          {snapshot && (
            <img
              src={snapshot}
              alt="Captured selfie"
              className="w-full h-full object-cover scale-x-[-1]"
              data-testid="kiosk-snapshot"
            />
          )}
        </div>
        <canvas ref={canvasRef} className="hidden" />
        <div className="flex gap-2 mt-3 max-w-sm mx-auto">
          {!snapshot && (
            <button
              onClick={capture}
              className="flex-1 h-11 rounded-sm border border-gold/40 bg-gold/15 hover:bg-gold/25 text-gold font-sans text-xs uppercase tracking-[0.14em] inline-flex items-center justify-center gap-1.5"
              data-testid="kiosk-capture"
            >
              <Camera className="w-4 h-4" /> Capture
            </button>
          )}
          {snapshot && (
            <>
              <button
                onClick={retake}
                className="flex-1 h-11 rounded-sm border border-white/10 hover:bg-white/[0.04] text-zinc-300 font-sans text-xs uppercase tracking-[0.14em]"
                data-testid="kiosk-retake"
              >
                Retake
              </button>
              <button
                onClick={submit}
                disabled={phase === "submitting"}
                className="flex-1 h-11 rounded-sm border border-emerald-500/40 bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-200 font-sans text-xs uppercase tracking-[0.14em] inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
                data-testid="kiosk-submit"
              >
                <Clock className="w-4 h-4" />
                {phase === "submitting" ? "Sending…" : "Confirm"}
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  return null;
}
